import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';

const MessagesTab = ({ setMessage, setError }) => {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [personalMessage, setPersonalMessage] = useState('');
    const [sendingPersonal, setSendingPersonal] = useState(false);
    const [broadcastMessage, setBroadcastMessage] = useState('');
    const [broadcastExpiry, setBroadcastExpiry] = useState('');
    const [broadcastAudience, setBroadcastAudience] = useState('active'); // New state for filter
    const [sendingBroadcast, setSendingBroadcast] = useState(false);
    const [broadcasts, setBroadcasts] = useState([]);
    const [customerSearch, setCustomerSearch] = useState('');
    const [powerQuote, setPowerQuote] = useState('');
    const [savingQuote, setSavingQuote] = useState(false);

    useEffect(() => {
        loadCustomers();
        loadBroadcasts();
    }, []);

    const loadCustomers = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/customers/enhanced', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCustomers(response.data.data.customers || []);
        } catch (error) {
            console.error('Failed to load customers:', error);
            setError('Failed to load customers');
        }
    };

    const loadBroadcasts = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.get('/admin/notifications/broadcasts', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBroadcasts(response.data.broadcasts || []);
        } catch (error) {
            console.error('Failed to load broadcasts:', error);
        } finally {
            setLoading(false);
        }
    };

    const sendPersonalMessage = async () => {
        if (!selectedCustomer || !personalMessage.trim()) {
            setError('Please select a customer and enter a message');
            return;
        }

        setSendingPersonal(true);
        try {
            const token = localStorage.getItem('token');
            await http.post('/admin/notifications/send-personal', {
                customer_id: selectedCustomer,
                message: personalMessage.trim()
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage('Message sent successfully');
            setPersonalMessage('');
            setSelectedCustomer('');
        } catch (error) {
            console.error('Failed to send personal message:', error);
            setError('Failed to send message');
        } finally {
            setSendingPersonal(false);
        }
    };

    const sendBroadcastMessage = async () => {
        if (!broadcastMessage.trim() || !broadcastExpiry) {
            setError('Please enter a message and select expiry date');
            return;
        }

        setSendingBroadcast(true);
        try {
            const token = localStorage.getItem('token');
            const response = await http.post('/admin/notifications/send-broadcast', {
                message: broadcastMessage.trim(),
                expiry_date: broadcastExpiry,
                filterType: broadcastAudience // Send filter type to backend
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage(`Broadcast sent to ${response.data.recipients_count || 0} customers`);
            setBroadcastMessage('');
            setBroadcastExpiry('');
            setBroadcastAudience('active'); // Reset filter
            await loadBroadcasts();
        } catch (error) {
            console.error('Failed to send broadcast:', error);
            setError('Failed to send broadcast');
        } finally {
            setSendingBroadcast(false);
        }
    };

    const deleteBroadcast = async (broadcastId) => {
        if (!confirm('Delete this broadcast message?')) return;

        try {
            const token = localStorage.getItem('token');
            await http.delete(`/admin/notifications/broadcast/${broadcastId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            setMessage('Broadcast deleted successfully');
            await loadBroadcasts();
        } catch (error) {
            console.error('Failed to delete broadcast:', error);
            setError('Failed to delete broadcast');
        }
    };

    const savePowerQuote = async () => {
        if (!powerQuote.trim()) {
            setError('Please enter a quote');
            return;
        }

        setSavingQuote(true);
        try {
            const token = localStorage.getItem('token');
            await http.post('/admin/power-quote',
                { quote: powerQuote.trim() },
                {
                    params: { quote: powerQuote.trim() },
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            setMessage('Power Quote set successfully! Customers will see it today.');
            setPowerQuote('');
        } catch (error) {
            console.error('Failed to save power quote:', error);
            setError('Failed to save power quote');
        } finally {
            setSavingQuote(false);
        }
    };

    const filteredCustomers = customers.filter(c =>
        `${c.first_name} ${c.last_name} ${c.email} ${c.address}`.toLowerCase().includes(customerSearch.toLowerCase())
    );

    const formatDate = (dateString) => {
        if (!dateString) return '';
        // By splitting the T on the raw DB string (if it's an ISO), or just returning it (if already 'YYYY-MM-DD'),
        // we prevent JS from passing it through the UTC converter and shifting it backwards out of Toronto timezone.
        return String(dateString).split('T')[0];
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-black to-gray-900 rounded-xl p-6 shadow-lg border-2 border-orange-500">
                <div className="flex justify-between items-center">
                    <div>
                        <h3 className="text-2xl font-bold text-white flex items-center gap-3">
                            <svg className="h-7 w-7 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                            </svg>
                            Customer Messages & Power Quote
                        </h3>
                        <p className="text-gray-300 text-sm mt-2">Send messages and set daily motivational quotes</p>
                    </div>
                    <button
                        onClick={loadBroadcasts}
                        className="bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white px-6 py-3 rounded-lg font-semibold shadow-lg transition-all"
                    >
                        Refresh
                    </button>
                </div>
            </div>

            {/* Power Quote Section */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-orange-500 overflow-hidden shadow-2xl">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                    <h4 className="font-semibold text-white text-lg flex items-center gap-2">
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Today's Power Quote
                    </h4>
                </div>
                <div className="p-6">
                    <p className="text-gray-300 mb-4">Set a daily motivational quote that all customers will see on their dashboard</p>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Power Quote (Replaces previous day's quote)
                            </label>
                            <textarea
                                value={powerQuote}
                                onChange={(e) => setPowerQuote(e.target.value)}
                                placeholder="Enter today's motivational quote..."
                                rows={3}
                                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        <button
                            onClick={savePowerQuote}
                            disabled={savingQuote || !powerQuote.trim()}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2"
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            {savingQuote ? 'Setting...' : 'Set Power Quote'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Send Personal Message */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-orange-500 overflow-hidden shadow-2xl">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                    <h4 className="font-semibold text-white text-lg">Send Message to Selected Customer</h4>
                </div>
                <div className="p-6">
                    <p className="text-gray-300 mb-4">Send a personal message directly to the customer's email</p>
                    <div className="space-y-4">
                        {/* Customer Search */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Search & Select Customer
                            </label>
                            <input
                                type="text"
                                value={customerSearch}
                                onChange={(e) => setCustomerSearch(e.target.value)}
                                placeholder="Search by name, email, or address..."
                                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                            <select
                                value={selectedCustomer}
                                onChange={(e) => setSelectedCustomer(e.target.value)}
                                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                                <option value="">-- Select Customer --</option>
                                {filteredCustomers.map(customer => {
                                    const city = customer.city && customer.city !== 'nan' ? customer.city : '';
                                    const postalCode = customer.postal_code && customer.postal_code !== 'nan' ? customer.postal_code : '';
                                    const addressDisplay = city || postalCode ? `(${[city, postalCode].filter(Boolean).join(', ')})` : '';

                                    return (
                                        <option key={customer.customer_id} value={customer.customer_id}>
                                            {customer.first_name} {customer.last_name} - {customer.email} {addressDisplay}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Message
                            </label>
                            <textarea
                                value={personalMessage}
                                onChange={(e) => setPersonalMessage(e.target.value)}
                                placeholder="Enter your message here..."
                                rows={4}
                                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        <button
                            onClick={sendPersonalMessage}
                            disabled={sendingPersonal || !selectedCustomer || !personalMessage.trim()}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2"
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {sendingPersonal ? 'Sending...' : 'Send Personal Message'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Broadcast Message */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-orange-500 overflow-hidden shadow-2xl">
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                    <h4 className="font-semibold text-white text-lg">Broadcast Message to All Customers</h4>
                </div>
                <div className="p-6">
                    <p className="text-gray-300 mb-4">Send a message that appears as a banner at the top of customer dashboards</p>
                    <div className="space-y-4">
                        {/* Target Audience Dropdown */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Target Audience
                            </label>
                            <select
                                value={broadcastAudience}
                                onChange={(e) => setBroadcastAudience(e.target.value)}
                                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 mb-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            >
                                <option value="active">Active Customers Only</option>
                                <option value="active_paused">Active & Paused Customers</option>
                                <option value="ex_customer">Ex-Customers Only</option>
                                <option value="all">Every Customer in Database</option>
                            </select>
                        </div>

                        {/* Message */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Broadcast Message
                            </label>
                            <textarea
                                value={broadcastMessage}
                                onChange={(e) => setBroadcastMessage(e.target.value)}
                                placeholder="Enter broadcast message here..."
                                rows={4}
                                className="w-full border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        {/* Expiry Date */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Expiry Date (message will be deleted after this date)
                            </label>
                            <input
                                type="date"
                                value={broadcastExpiry}
                                onChange={(e) => setBroadcastExpiry(e.target.value)}
                                min={new Date().toISOString().split('T')[0]}
                                className="border-2 border-gray-600 bg-gray-800 text-white rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                            />
                        </div>

                        <button
                            onClick={sendBroadcastMessage}
                            disabled={sendingBroadcast || !broadcastMessage.trim() || !broadcastExpiry}
                            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold shadow-lg transition-all flex items-center gap-2"
                        >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                            </svg>
                            {sendingBroadcast ? 'Sending...' : 'Send Broadcast'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Active Broadcasts */}
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-xl border-2 border-orange-500 p-6 shadow-2xl">
                <h4 className="font-semibold text-white mb-4 text-lg">Active Broadcast Messages</h4>
                {loading ? (
                    <p className="text-gray-300">Loading broadcasts...</p>
                ) : broadcasts.length === 0 ? (
                    <p className="text-gray-300">No active broadcasts</p>
                ) : (
                    <div className="space-y-3">
                        {broadcasts.map(broadcast => (
                            <div key={broadcast.id} className="border border-gray-600 rounded-md p-4 bg-gray-900">
                                <div className="flex justify-between items-start">
                                    <div className="flex-1">
                                        <p className="text-white mb-2">{broadcast.message}</p>
                                        <p className="text-sm text-gray-900">
                                            Expires: {formatDate(broadcast.expiry_date)} |
                                            Created: {formatDate(broadcast.created_at)}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => deleteBroadcast(broadcast.id)}
                                        className="ml-4 bg-orange-500 hover:bg-orange-600 text-white px-3 py-1 rounded text-sm"
                                    >
                                        🗑️ Delete
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessagesTab;
